import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNotificationBar } from '../../../../context/notificationContext'
import { getErrorResponse } from '../../../../services/util'
import { useCreateAppVersion, useUpdateAppVersion } from 'hooks/app-version'
import InputField from '../../../../components/styledFormElements/InputField'

interface AppVersionModalProps {
    isOpen: boolean
    onClose: () => void
    editData?: any
    onSuccess: () => void
}

const AppVersionModal: React.FC<AppVersionModalProps> = ({
    isOpen,
    onClose,
    editData,
    onSuccess,
}) => {
    const notification = useNotificationBar()
    const { mutate: createAppVersion } = useCreateAppVersion()
    const { mutate: updateAppVersion } = useUpdateAppVersion()

    const useFormHook = useForm({
        defaultValues: {
            platform: '',
            version: '',
            description: '',
            is_latest: false,
            force_update: false,
        },
    })

    useEffect(() => {
        if (editData) {
            useFormHook.reset({
                platform: editData.platform || '',
                version: editData.version || '',
                description: editData.description || '',
                is_latest: editData.is_latest === 1 || editData.is_latest === true,
                force_update: editData.force_update === 1 || editData.force_update === true,
            })
        } else {
            useFormHook.reset({
                platform: '',
                version: '',
                description: '',
                is_latest: false,
                force_update: false,
            })
        }
    }, [editData, useFormHook])

    const onSubmit = (data: any) => {
        const payload = {
            platform: data.platform,
            version: data.version,
            description: data.description,
            is_latest: data.is_latest ? 1 : 0,
            force_update: data.force_update ? 1 : 0,
        }

        if (editData) {
            // Update existing version
            updateAppVersion({
                id: editData.id,
                data: payload
            }, {
                onSuccess: () => {
                    notification.push({
                        title: 'Success!',
                        message: 'App version updated successfully!',
                        type: 'success',
                    })
                    onSuccess()
                    onClose()
                    useFormHook.reset()
                },
                onError: (error) => {
                    notification.push(getErrorResponse(error))
                },
            })
        } else {
            // Create new version
            createAppVersion(payload, {
                onSuccess: () => {
                    notification.push({
                        title: 'Success!',
                        message: 'App version created successfully!',
                        type: 'success',
                    })
                    onSuccess()
                    onClose()
                    useFormHook.reset()
                },
                onError: (error) => {
                    notification.push(getErrorResponse(error))
                },
            })
        }
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-lg p-6 w-full max-w-md mx-4">
                <h2 className="text-xl font-semibold mb-4 dark:text-white">
                    {editData ? 'Edit App Version' : 'Create App Version'}
                </h2>

                <form onSubmit={useFormHook.handleSubmit(onSubmit)} className="space-y-4">
                    {/* Platform Select */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
                            Platform <span className="text-red-700">*</span>
                        </label>
                        <select
                            {...useFormHook.register('platform', { 
                                required: 'Platform is required' 
                            })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                            <option value="">Select Platform</option>
                            <option value="ios">iOS</option>
                            <option value="android">Android</option>
                        </select>
                        {useFormHook.formState.errors.platform && (
                            <p className="text-xs text-red-600 mt-1">
                                {useFormHook.formState.errors.platform.message as string}
                            </p>
                        )}
                    </div>

                    {/* Version Input */}
                <InputField
                    label="Version"
                    placeholder="e.g., 1.2 or 1.2.3"
                    register={useFormHook.register('version', { 
                        required: 'Version is required',
                        pattern: {
                            value: /^\d+\.\d+(\.\d+)?$/,
                            message: 'Version must be in format x.y or x.y.z (e.g., 1.2 or 1.2.3)'
                        }
                    })}
                    error={useFormHook.formState.errors.version?.message as string}
                    required
                />

                    {/* Description Textarea */}
                    <InputField
                        label="Description"
                        placeholder="Enter version description"
                        register={useFormHook.register('description', { 
                            required: 'Description is required',
                            minLength: {
                                value: 10,
                                message: 'Description must be at least 10 characters'
                            }
                        })}
                        error={useFormHook.formState.errors.description?.message as string}
                        isTextarea
                        row={4}
                        required
                    />

                    {/* Is Latest Checkbox */}
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="is_latest"
                            {...useFormHook.register('is_latest')}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="is_latest" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                            Mark as Latest Version
                        </label>
                    </div>

                    {/* Force Update Checkbox */}
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="force_update"
                            {...useFormHook.register('force_update')}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="force_update" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                            Force Update
                        </label>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            {editData ? 'Update' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default AppVersionModal
